import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import { clientService } from '../../services/clientService';
import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
  recepcionado: 'bg-blue-100 text-blue-700',
  inspeccionado: 'bg-purple-100 text-purple-700',
  cotizando: 'bg-yellow-100 text-yellow-700',
  aprobado: 'bg-green-100 text-green-700',
  enproceso: 'bg-orange-100 text-orange-700',
  terminado: 'bg-teal-100 text-teal-700',
  entregado: 'bg-gray-100 text-gray-700',
  cancelado: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  recepcionado: 'Recepcionado',
  inspeccionado: 'Inspeccionado',
  cotizando: 'Cotizando',
  aprobado: 'Aprobado',
  enproceso: 'En Proceso',
  terminado: 'Terminado',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

const INSP_STATUS_COLORS = {
  bueno: 'bg-green-100 text-green-700',
  requiere_atencion: 'bg-yellow-100 text-yellow-700',
  critico: 'bg-red-100 text-red-700',
};
const INSP_STATUS_LABELS = {
  bueno: 'Bueno',
  requiere_atencion: 'Requiere Atención',
  critico: 'Crítico',
};

const fmtDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';
const fmtTime = (d) =>
  d ? new Date(d)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDateTime = (d) =>
  d ? `${fmtDate(d)} ${fmtTime(d)}` : '—';

// ─── Próximo Servicio Recomendado — lógica de derivación ─────────────────────
/**
 * Deriva una recomendación de próximo servicio a partir del historial de OTs e inspecciones.
 * Retorna null si no hay suficiente información.
 *
 * @param {object[]} workOrders  - Array de órdenes de trabajo del vehículo
 * @param {object[]} inspections - Array de inspecciones técnicas del vehículo
 * @param {object|null} vehicle  - Datos del vehículo (para kilometraje actual)
 * @returns {{ servicio, motivo, fechaRecomendada, kmRecomendado, prioridad } | null}
 */
const deriveProximoServicio = (workOrders = [], inspections = [], vehicle = null) => {
  // ── 1. Inspecciones críticas o con recomendaciones pendientes ────────────────
  const inspCritica = inspections?.find(i => i?.status === 'critico');
  const inspAtencion = inspections?.find(i => i?.status === 'requiere_atencion');
  const inspConRec = inspections?.find(i => i?.recommendations && i?.recommendations?.trim()?.length > 5);

  if (inspCritica) {
    return {
      servicio: inspCritica?.category || 'Revisión urgente',
      motivo: inspCritica?.recommendations || inspCritica?.findings || 'Condición crítica detectada en inspección técnica.',
      fechaRecomendada: new Date()?.toISOString()?.split('T')?.[0],
      kmRecomendado: null,
      prioridad: 'alta',
    };
  }

  if (inspAtencion) {
    const fechaBase = inspAtencion?.inspected_at ? new Date(inspAtencion?.inspected_at) : new Date();
    fechaBase?.setDate(fechaBase?.getDate() + 14);
    return {
      servicio: inspAtencion?.category || 'Revisión preventiva',
      motivo: inspAtencion?.recommendations || inspAtencion?.findings || 'Se detectaron elementos que requieren atención.',
      fechaRecomendada: fechaBase?.toISOString()?.split('T')?.[0],
      kmRecomendado: null,
      prioridad: 'media',
    };
  }

  // ── 2. Basado en la última OT entregada — inferir próximo servicio ───────────
  const ultimaOT = workOrders?.find(wo => wo?.estado_ot === 'entregado' || wo?.estado_ot === 'terminado');

  if (ultimaOT) {
    const motivo = (ultimaOT?.motivo_ingreso || '')?.toLowerCase();
    const notas = (ultimaOT?.notas || '')?.toLowerCase();
    const texto = `${motivo} ${notas}`;

    // Detectar tipo de servicio realizado para sugerir el siguiente
    let servicio = 'Servicio de mantenimiento preventivo';
    let motivoRec = 'Basado en el historial de servicios realizados.';
    let intervaloDias = 180; // 6 meses por defecto
    let intervaloKm = 5000;
    let prioridad = 'baja';

    if (/aceite|oil|lubricante|cambio de aceite/?.test(texto)) {
      servicio = 'Cambio de aceite y filtro';
      motivoRec = 'El último cambio de aceite fue registrado. Se recomienda el siguiente según intervalo estándar.';
      intervaloDias = 90;
      intervaloKm = 5000;
      prioridad = 'media';
    } else if (/frenos|balatas|pastillas|disco/?.test(texto)) {
      servicio = 'Revisión de sistema de frenos';
      motivoRec = 'Se realizó trabajo en frenos. Se recomienda revisión de seguimiento.';
      intervaloDias = 180;
      intervaloKm = 10000;
      prioridad = 'media';
    } else if (/llanta|neumatico|neumático|rueda/?.test(texto)) {
      servicio = 'Rotación y revisión de llantas';
      motivoRec = 'Se realizó trabajo en llantas. Se recomienda rotación preventiva.';
      intervaloDias = 180;
      intervaloKm = 10000;
      prioridad = 'baja';
    } else if (/afinacion|afinación|bujia|bujía|filtro de aire/?.test(texto)) {
      servicio = 'Afinación mayor';
      motivoRec = 'Se realizó afinación. Se recomienda la siguiente según intervalo del fabricante.';
      intervaloDias = 365;
      intervaloKm = 20000;
      prioridad = 'baja';
    } else if (/transmision|transmisión|caja/?.test(texto)) {
      servicio = 'Revisión de transmisión';
      motivoRec = 'Se realizó trabajo en transmisión. Se recomienda revisión preventiva.';
      intervaloDias = 365;
      intervaloKm = 20000;
      prioridad = 'media';
    } else if (/suspension|suspensión|amortiguador/?.test(texto)) {
      servicio = 'Revisión de suspensión';
      motivoRec = 'Se realizó trabajo en suspensión. Se recomienda revisión preventiva.';
      intervaloDias = 365;
      intervaloKm = 15000;
      prioridad = 'baja';
    } else if (/electrico|eléctrico|bateria|batería|alternador/?.test(texto)) {
      servicio = 'Revisión del sistema eléctrico';
      motivoRec = 'Se realizó trabajo eléctrico. Se recomienda revisión preventiva del sistema.';
      intervaloDias = 365;
      intervaloKm = 20000;
      prioridad = 'baja';
    }

    const fechaBase = ultimaOT?.fecha_entrega
      ? new Date(ultimaOT?.fecha_entrega)
      : ultimaOT?.created_at
        ? new Date(ultimaOT?.created_at)
        : new Date();
    fechaBase?.setDate(fechaBase?.getDate() + intervaloDias);

    const kmActual = vehicle?.kilometraje || vehicle?.mileage || ultimaOT?.kilometraje || null;
    const kmRec = kmActual ? kmActual + intervaloKm : null;

    return {
      servicio,
      motivo: motivoRec,
      fechaRecomendada: fechaBase?.toISOString()?.split('T')?.[0],
      kmRecomendado: kmRec,
      prioridad,
    };
  }

  // ── 3. Si hay inspección con recomendaciones pero sin OT entregada ───────────
  if (inspConRec) {
    const fechaBase = inspConRec?.inspected_at ? new Date(inspConRec?.inspected_at) : new Date();
    fechaBase?.setDate(fechaBase?.getDate() + 30);
    return {
      servicio: inspConRec?.category || 'Servicio recomendado por inspección',
      motivo: inspConRec?.recommendations,
      fechaRecomendada: fechaBase?.toISOString()?.split('T')?.[0],
      kmRecomendado: null,
      prioridad: 'media',
    };
  }

  return null;
};

// ─── Bloque Próximo Servicio Recomendado ─────────────────────────────────────
const PRIORIDAD_CONFIG = {
  alta: { label: 'Alta', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: 'AlertTriangle', iconColor: 'text-red-500', headerBg: 'bg-red-50/80' },
  media: { label: 'Media', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: 'AlertCircle', iconColor: 'text-amber-500', headerBg: 'bg-amber-50/80' },
  baja: { label: 'Baja', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', icon: 'Info', iconColor: 'text-emerald-500', headerBg: 'bg-emerald-50/80' },
};

const ProximoServicioBlock = ({ recomendacion }) => {
  if (!recomendacion) return null;

  const cfg = PRIORIDAD_CONFIG?.[recomendacion?.prioridad] || PRIORIDAD_CONFIG?.baja;

  return (
    <div className={`rounded-lg border ${cfg?.border} overflow-hidden`}>
      {/* Header */}
      <div className={`${cfg?.headerBg} border-b ${cfg?.border} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon name="CalendarClock" size={16} className={cfg?.iconColor} />
          <span className="text-sm font-semibold text-foreground">Próximo Servicio Recomendado</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cfg?.badge}`}>
          Prioridad {cfg?.label}
        </span>
      </div>

      {/* Body */}
      <div className={`${cfg?.bg} px-4 py-4 space-y-3`}>
        {/* Servicio sugerido */}
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 p-1.5 rounded-md bg-white/70`}>
            <Icon name="Wrench" size={14} className="text-foreground/60" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Servicio sugerido</p>
            <p className="text-sm font-semibold text-foreground">{recomendacion?.servicio}</p>
          </div>
        </div>

        {/* Motivo */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-1.5 rounded-md bg-white/70">
            <Icon name="MessageSquare" size={14} className="text-foreground/60" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Motivo</p>
            <p className="text-sm text-foreground">{recomendacion?.motivo}</p>
          </div>
        </div>

        {/* Fecha y km en fila */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {recomendacion?.fechaRecomendada && (
            <div className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2">
              <Icon name="Calendar" size={14} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Fecha recomendada</p>
                <p className="text-sm font-medium text-foreground">{fmtDate(recomendacion?.fechaRecomendada)}</p>
              </div>
            </div>
          )}
          {recomendacion?.kmRecomendado && (
            <div className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2">
              <Icon name="Gauge" size={14} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Kilometraje recomendado</p>
                <p className="text-sm font-medium text-foreground">{recomendacion?.kmRecomendado?.toLocaleString('es-MX')} km</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Acciones desde Historial ─────────────────────────────────────────────────
const AccionesHistorial = ({ wo, vehicle, client, inspections = [], items = [], navigate }) => {
  const [alertMsg, setAlertMsg] = useState('');

  const buildPrefilledData = () => {
    // Gather inspections related to this OT
    const inspRelacionadas = inspections?.filter(i => i?.work_order_id === wo?.id);
    const hallazgos = inspRelacionadas
      ?.map(i => [i?.category && `[${i?.category}]`, i?.findings]?.filter(Boolean)?.join(' '))
      ?.filter(Boolean)
      ?.join('\n');
    const recomendaciones = inspRelacionadas
      ?.map(i => i?.recommendations)
      ?.filter(Boolean)
      ?.join('\n');

    // Separate approved vs pending items
    const trabajosRealizados = items?.filter(i => i?.is_approved);
    const trabajosPendientes = items?.filter(i => !i?.is_approved);

    return {
      // Core IDs
      clientId: client?.id || wo?.client_id,
      vehicleId: vehicle?.id || wo?.vehicle_id,
      // Pre-fill fields
      kilometraje: wo?.kilometraje || '',
      motivoIngreso: wo?.motivo_ingreso || '',
      observaciones: wo?.notas || '',
      hallazgos: hallazgos || '',
      recomendaciones: recomendaciones || '',
      trabajosRealizados,
      trabajosPendientes,
      // Reference
      origenHistorialId: wo?.id,
      origenFolio: wo?.folio_ot,
      origenLabel: 'Generado desde historial',
      // Client/vehicle display
      clienteNombre: client?.nombre || client?.empresa || '',
      vehicleDisplay: vehicle ? `${vehicle?.marca || ''} ${vehicle?.modelo || ''} ${vehicle?.year || ''}`?.trim() : '',
      vehiclePlacas: vehicle?.placas || '',
    };
  };

  const validate = () => {
    const missing = [];
    if (!client?.id && !wo?.client_id) missing?.push('cliente');
    if (!vehicle?.id && !wo?.vehicle_id) missing?.push('vehículo');
    return missing;
  };

  const handleGenerarOT = () => {
    const missing = validate();
    if (missing?.length > 0) {
      setAlertMsg(`Faltan datos para generar la OT: ${missing?.join(', ')}.`);
      return;
    }
    setAlertMsg('');
    const prefilledData = buildPrefilledData();
    navigate('/vehicle-reception', {
      state: {
        fromHistorial: true,
        prefilledClientId: prefilledData?.clientId,
        prefilledVehicleId: prefilledData?.vehicleId,
        prefilledKilometraje: prefilledData?.kilometraje,
        prefilledMotivoIngreso: prefilledData?.motivoIngreso,
        prefilledObservaciones: prefilledData?.observaciones,
        origenHistorialId: prefilledData?.origenHistorialId,
        origenFolio: prefilledData?.origenFolio,
        origenLabel: prefilledData?.origenLabel,
      },
    });
  };

  const handleGenerarCotizacion = () => {
    const missing = validate();
    if (missing?.length > 0) {
      setAlertMsg(`Faltan datos para generar la cotización: ${missing?.join(', ')}.`);
      return;
    }
    setAlertMsg('');
    const prefilledData = buildPrefilledData();
    // Build a workOrder-like object that quotation-management expects via location.state
    const workOrderState = {
      id: null, // new document
      folio_ot: null,
      client_id: prefilledData?.clientId,
      vehicle_id: prefilledData?.vehicleId,
      motivo_ingreso: prefilledData?.motivoIngreso,
      notas: prefilledData?.observaciones,
      kilometraje: prefilledData?.kilometraje,
      client: { nombre: prefilledData?.clienteNombre },
      vehicle: vehicle || {},
      origenHistorialId: prefilledData?.origenHistorialId,
      origenFolio: prefilledData?.origenFolio,
      origenLabel: prefilledData?.origenLabel,
      hallazgos: prefilledData?.hallazgos,
      recomendaciones: prefilledData?.recomendaciones,
      trabajosRealizados: prefilledData?.trabajosRealizados,
      trabajosPendientes: prefilledData?.trabajosPendientes,
    };
    navigate('/quotation-management', {
      state: {
        fromHistorial: true,
        prefilledClientId: prefilledData?.clientId,
        prefilledVehicleId: prefilledData?.vehicleId,
        workOrder: workOrderState,
        origenHistorialId: prefilledData?.origenHistorialId,
        origenFolio: prefilledData?.origenFolio,
        origenLabel: prefilledData?.origenLabel,
      },
    });
  };

  const handleGenerarNV = () => {
    const missing = validate();
    if (missing?.length > 0) {
      setAlertMsg(`Faltan datos para generar la Nota de Venta: ${missing?.join(', ')}.`);
      return;
    }
    setAlertMsg('');
    const prefilledData = buildPrefilledData();
    // Build a saleNote-like prefill object for sale-note-detail
    const saleNoteState = {
      clientId: prefilledData?.clientId,
      vehicleId: prefilledData?.vehicleId,
      clienteNombre: prefilledData?.clienteNombre,
      vehicleDisplay: prefilledData?.vehicleDisplay,
      vehiclePlacas: prefilledData?.vehiclePlacas,
      motivoIngreso: prefilledData?.motivoIngreso,
      observaciones: prefilledData?.observaciones,
      hallazgos: prefilledData?.hallazgos,
      recomendaciones: prefilledData?.recomendaciones,
      kilometraje: prefilledData?.kilometraje,
      origenHistorialId: prefilledData?.origenHistorialId,
      origenFolio: prefilledData?.origenFolio,
      origenLabel: prefilledData?.origenLabel,
      trabajosRealizados: prefilledData?.trabajosRealizados,
      trabajosPendientes: prefilledData?.trabajosPendientes,
      status: 'draft',
    };
    navigate('/sale-note-detail', {
      state: {
        fromHistorial: true,
        saleNote: saleNoteState,
        workOrder: {
          client_id: prefilledData?.clientId,
          vehicle_id: prefilledData?.vehicleId,
          motivo_ingreso: prefilledData?.motivoIngreso,
          notas: prefilledData?.observaciones,
          kilometraje: prefilledData?.kilometraje,
          origenHistorialId: prefilledData?.origenHistorialId,
          origenFolio: prefilledData?.origenFolio,
          origenLabel: prefilledData?.origenLabel,
        },
      },
    });
  };

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 overflow-hidden">
      {/* Header */}
      <div className="bg-primary/10 border-b border-primary/20 px-4 py-3 flex items-center gap-2">
        <Icon name="Zap" size={16} className="text-primary" />
        <span className="text-sm font-semibold text-primary">Acciones desde historial</span>
        <span className="ml-auto text-xs text-muted-foreground bg-background/60 px-2 py-0.5 rounded-full">
          Generado desde historial
        </span>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* Alert */}
        {alertMsg && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <Icon name="AlertCircle" size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{alertMsg}</p>
          </div>
        )}

        {/* Info row */}
        <p className="text-xs text-muted-foreground">
          Crea un nuevo documento usando los datos de esta OT. El registro original no se modifica.
        </p>

        {/* Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Generar OT */}
          <button
            onClick={handleGenerarOT}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 active:scale-95 transition-all group"
          >
            <div className="p-2.5 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <Icon name="ClipboardPlus" size={22} className="text-blue-600" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-blue-700">Generar OT</p>
              <p className="text-xs text-blue-500 mt-0.5">Nueva orden de trabajo</p>
            </div>
          </button>

          {/* Generar Cotización */}
          <button
            onClick={handleGenerarCotizacion}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-violet-50 border border-violet-200 hover:bg-violet-100 hover:border-violet-300 active:scale-95 transition-all group"
          >
            <div className="p-2.5 bg-violet-100 rounded-lg group-hover:bg-violet-200 transition-colors">
              <Icon name="FileText" size={22} className="text-violet-600" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-violet-700">Generar Cotización</p>
              <p className="text-xs text-violet-500 mt-0.5">Nueva cotización</p>
            </div>
          </button>

          {/* Generar NV */}
          <button
            onClick={handleGenerarNV}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 active:scale-95 transition-all group"
          >
            <div className="p-2.5 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
              <Icon name="Receipt" size={22} className="text-emerald-600" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-emerald-700">Generar NV</p>
              <p className="text-xs text-emerald-500 mt-0.5">Nueva nota de venta</p>
            </div>
          </button>
        </div>

        {/* Reference badge */}
        {wo?.folio_ot && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background/60 rounded-lg px-3 py-2">
            <Icon name="Link" size={12} className="text-primary flex-shrink-0" />
            <span>Referencia origen: <span className="font-mono font-semibold text-foreground">{wo?.folio_ot}</span></span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── OT Detail Modal ──────────────────────────────────────────────────────────
const OTDetailModal = ({ wo, onClose, inspections = [], vehicle = null, client = null, navigate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wo?.id) return;
    const fetchItems = async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          ?.from('quotation_items')
          ?.select('id, description, quantity, unit_price, total_price, is_approved, category, created_at')
          ?.eq('work_order_id', wo?.id)
          ?.order('created_at', { ascending: true });
        setItems(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [wo?.id]);

  const trabajos = items?.filter(i => i?.category === 'labor' || i?.category === 'mano_de_obra' || (!i?.category && i?.description));
  const refacciones = items?.filter(i => i?.category === 'parts' || i?.category === 'refaccion' || i?.category === 'part');
  const otrosItems = items?.filter(i => !trabajos?.includes(i) && !refacciones?.includes(i));

  // Recomendación de próximo servicio basada en esta OT e inspecciones relacionadas
  const inspRelacionadas = inspections?.filter(i => i?.work_order_id === wo?.id);
  const recomendacion = deriveProximoServicio([wo], inspRelacionadas, vehicle);

  // Cronología de la OT
  const cronologia = [];
  if (wo?.created_at) cronologia?.push({ fecha: wo?.created_at, evento: 'OT creada', icono: 'FilePlus', color: 'text-blue-500', bg: 'bg-blue-50' });
  if (wo?.fecha_ingreso) cronologia?.push({ fecha: wo?.fecha_ingreso, evento: 'Vehículo recepcionado', icono: 'Car', color: 'text-indigo-500', bg: 'bg-indigo-50' });
  if (wo?.estado_ot === 'inspeccionado' || wo?.estado_ot === 'cotizando' || wo?.estado_ot === 'aprobado' || wo?.estado_ot === 'enproceso' || wo?.estado_ot === 'terminado' || wo?.estado_ot === 'entregado') {
    cronologia?.push({ fecha: wo?.updated_at || wo?.created_at, evento: `Estado: ${STATUS_LABELS?.[wo?.estado_ot] || wo?.estado_ot}`, icono: 'RefreshCw', color: 'text-orange-500', bg: 'bg-orange-50' });
  }
  if (wo?.fecha_entrega) cronologia?.push({ fecha: wo?.fecha_entrega, evento: 'Vehículo entregado', icono: 'Truck', color: 'text-green-500', bg: 'bg-green-50' });
  cronologia?.sort((a, b) => new Date(a?.fecha) - new Date(b?.fecha));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Icon name="ClipboardList" size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-lg">Detalle de Orden de Trabajo</h2>
              <p className="text-sm text-muted-foreground font-mono">{wo?.folio_ot}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Estatus y fechas */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Estatus</p>
              <span className={`text-xs px-2 py-1 rounded-full font-semibold ${STATUS_COLORS?.[wo?.estado_ot] || 'bg-gray-100 text-gray-700'}`}>
                {STATUS_LABELS?.[wo?.estado_ot] || wo?.estado_ot || '—'}
              </span>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Fecha de Ingreso</p>
              <p className="text-sm font-medium text-foreground">{fmtDate(wo?.fecha_ingreso)}</p>
              <p className="text-xs text-muted-foreground">{fmtTime(wo?.fecha_ingreso)}</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Fecha de Entrega</p>
              <p className="text-sm font-medium text-foreground">{fmtDate(wo?.fecha_entrega)}</p>
              <p className="text-xs text-muted-foreground">{fmtTime(wo?.fecha_entrega)}</p>
            </div>
            {wo?.kilometraje && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Kilometraje</p>
                <p className="text-sm font-medium text-foreground">{wo?.kilometraje?.toLocaleString('es-MX')} km</p>
              </div>
            )}
            {wo?.total > 0 && (
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-700 mb-1">Total</p>
                <p className="text-sm font-bold text-emerald-700">${parseFloat(wo?.total)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
              </div>
            )}
          </div>

          {/* Motivo / Observaciones */}
          {(wo?.motivo_ingreso || wo?.notas) && (
            <div className="space-y-3">
              {wo?.motivo_ingreso && (
                <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="MessageSquare" size={15} className="text-blue-600" />
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Motivo de Ingreso</p>
                  </div>
                  <p className="text-sm text-foreground">{wo?.motivo_ingreso}</p>
                </div>
              )}
              {wo?.notas && (
                <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="StickyNote" size={15} className="text-amber-600" />
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Observaciones</p>
                  </div>
                  <p className="text-sm text-foreground">{wo?.notas}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Acciones desde historial ── */}
          <AccionesHistorial
            wo={wo}
            vehicle={vehicle}
            client={client}
            inspections={inspections}
            items={items}
            navigate={navigate}
          />

          {/* Trabajos realizados */}
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">Cargando trabajos…</span>
            </div>
          ) : (
            <>
              {/* Trabajos / Mano de obra */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Wrench" size={16} className="text-orange-500" />
                  <h3 className="font-semibold text-sm text-foreground uppercase tracking-wide">Trabajos Realizados</h3>
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600">{trabajos?.length || items?.length}</span>
                </div>
                {(trabajos?.length > 0 ? trabajos : items)?.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Sin trabajos registrados</p>
                ) : (
                  <ul className="space-y-2">
                    {(trabajos?.length > 0 ? trabajos : items)?.map((item, idx) => (
                      <li key={item?.id || idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                        <span className="text-xs text-muted-foreground font-mono mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{item?.description || '—'}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                            {item?.quantity > 0 && (
                              <span className="text-xs text-muted-foreground">Cant: {item?.quantity}</span>
                            )}
                            {item?.unit_price > 0 && (
                              <span className="text-xs text-muted-foreground">P.U.: ${parseFloat(item?.unit_price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            )}
                            {item?.total_price > 0 && (
                              <span className="text-xs text-emerald-600 font-medium">Total: ${parseFloat(item?.total_price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            )}
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                              item?.is_approved ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {item?.is_approved ? 'Aprobado' : 'Pendiente'}
                            </span>
                          </div>
                          {item?.created_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              <Icon name="Clock" size={10} className="inline mr-1" />
                              {fmtDateTime(item?.created_at)}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Refacciones */}
              {refacciones?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Package" size={16} className="text-blue-500" />
                    <h3 className="font-semibold text-sm text-foreground uppercase tracking-wide">Refacciones Cambiadas</h3>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{refacciones?.length}</span>
                  </div>
                  <ul className="space-y-2">
                    {refacciones?.map((item, idx) => (
                      <li key={item?.id || idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                        <span className="text-xs text-muted-foreground font-mono mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{item?.description || '—'}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                            {item?.quantity > 0 && (
                              <span className="text-xs text-muted-foreground">Cant: {item?.quantity}</span>
                            )}
                            {item?.total_price > 0 && (
                              <span className="text-xs text-emerald-600 font-medium">Total: ${parseFloat(item?.total_price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Historial cronológico */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Clock" size={16} className="text-primary" />
              <h3 className="font-semibold text-sm text-foreground uppercase tracking-wide">Historial Cronológico</h3>
            </div>
            {cronologia?.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Sin eventos registrados</p>
            ) : (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                {cronologia?.map((ev, idx) => (
                  <div key={idx} className="relative flex gap-4 pb-4">
                    <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full ${ev?.bg} flex items-center justify-center border-2 border-background`}>
                      <Icon name={ev?.icono} size={13} className={ev?.color} />
                    </div>
                    <div className="flex-1 bg-muted/30 rounded-lg p-3 min-w-0">
                      <p className="text-sm font-medium text-foreground">{ev?.evento}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <Icon name="Calendar" size={10} className="inline mr-1" />
                        {fmtDate(ev?.fecha)}
                        <span className="mx-1">·</span>
                        <Icon name="Clock" size={10} className="inline mr-1" />
                        {fmtTime(ev?.fecha)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Qué falta por hacer */}
          {wo?.estado_ot && wo?.estado_ot !== 'entregado' && wo?.estado_ot !== 'cancelado' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertCircle" size={15} className="text-amber-600" />
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Pendiente</p>
              </div>
              <p className="text-sm text-amber-800">
                {wo?.estado_ot === 'recepcionado' && 'Pendiente de inspección técnica.'}
                {wo?.estado_ot === 'inspeccionado' && 'Pendiente de cotización al cliente.'}
                {wo?.estado_ot === 'cotizando' && 'Esperando aprobación del cliente.'}
                {wo?.estado_ot === 'aprobado' && 'Pendiente de iniciar trabajos.'}
                {wo?.estado_ot === 'enproceso' && 'Trabajos en proceso, pendiente de terminar.'}
                {wo?.estado_ot === 'terminado' && 'Trabajos terminados, pendiente de entrega al cliente.'}
              </p>
            </div>
          )}

          {/* Próximo Servicio Recomendado */}
          <ProximoServicioBlock recomendacion={recomendacion} />
        </div>
      </div>
    </div>
  );
};

// ─── Fuzzy helpers ──────────────────────────────────────────────────────────────
const normalizeStr = (s) =>
  (s || '')?.toLowerCase()?.trim()?.normalize('NFD')?.replace(/\p{Mn}/gu, '');

const levenshtein = (a, b) => {
  const m = a?.length, n = b?.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a?.[i - 1] === b?.[j - 1]
        ? dp?.[i - 1]?.[j - 1]
        : 1 + Math.min(dp?.[i - 1]?.[j], dp?.[i]?.[j - 1], dp?.[i - 1]?.[j - 1]);
    }
  }
  return dp?.[m]?.[n];
};

const fuzzyScore = (query, target) => {
  const q = normalizeStr(query);
  const t = normalizeStr(target);
  if (!q || !t) return 0;
  const words = t?.split(/\s+/);
  if (words?.some(w => w?.includes(q) || q?.includes(w))) return 1;
  if (t?.includes(q)) return 1;
  const dist = levenshtein(q, t);
  const maxLen = Math.max(q?.length, t?.length);
  const simFull = 1 - dist / maxLen;
  const wordSim = Math.max(...words?.map(w => {
    const d = levenshtein(q, w);
    return 1 - d / Math.max(q?.length, w?.length);
  }));
  return Math.max(simFull, wordSim);
};
// ── End fuzzy helpers ──────────────────────────────────────────────────────────

const SmartSearch = ({ onSelectVehicle }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [fuzzyResults, setFuzzyResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchMode, setSearchMode] = useState(null); // 'vin' | 'placas' | 'cliente'
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Detect search mode from query
  const detectMode = (q) => {
    const trimmed = q?.trim()?.toUpperCase();
    // VIN: 17 alphanumeric chars
    if (/^[A-HJ-NPR-Z0-9]{5,17}$/?.test(trimmed) && trimmed?.length >= 5 && !trimmed?.includes(' ')) {
      return 'vin';
    }
    // Plates: typical MX format (letters+numbers, no spaces, 5-8 chars) or explicit
    if (/^[A-Z0-9]{5,8}$/?.test(trimmed) && trimmed?.length <= 8) {
      return 'placas';
    }
    // Default: client name (has spaces or longer text)
    return 'cliente';
  };

  const doSearch = useCallback(async (q) => {
    if (!q || q?.trim()?.length < 2) {
      setResults([]);
      setFuzzyResults([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    const trimmed = q?.trim();
    const mode = detectMode(trimmed);
    setSearchMode(mode);

    try {
      let found = [];

      if (mode === 'vin') {
        // Search vehicles by VIN (partial match)
        const { data } = await supabase
          ?.from('vehicles')
          ?.select('id, vin, placas, marca, modelo, year, client_id, client:clients(id, nombre, empresa, telefono)')
          ?.ilike('vin', `%${trimmed}%`)
          ?.limit(10);
        found = (data || [])?.map(v => ({
          type: 'vehicle',
          vehicleId: v?.id,
          clientId: v?.client_id,
          vin: v?.vin,
          placas: v?.placas,
          marca: v?.marca,
          modelo: v?.modelo,
          year: v?.year,
          clienteNombre: v?.client?.nombre || v?.client?.empresa || '',
          clienteTelefono: v?.client?.telefono || '',
        }));
        setFuzzyResults([]);
      } else if (mode === 'placas') {
        // Search vehicles by plates (partial match)
        const { data } = await supabase
          ?.from('vehicles')
          ?.select('id, vin, placas, marca, modelo, year, client_id, client:clients(id, nombre, empresa, telefono)')
          ?.ilike('placas', `%${trimmed}%`)
          ?.limit(10);
        found = (data || [])?.map(v => ({
          type: 'vehicle',
          vehicleId: v?.id,
          clientId: v?.client_id,
          vin: v?.vin,
          placas: v?.placas,
          marca: v?.marca,
          modelo: v?.modelo,
          year: v?.year,
          clienteNombre: v?.client?.nombre || v?.client?.empresa || '',
          clienteTelefono: v?.client?.telefono || '',
        }));
        setFuzzyResults([]);
      } else {
        // ── 1) Exact/ILIKE search ──────────────────────────────────────────────
        const { data: exactClients } = await supabase
          ?.from('clients')
          ?.select('id, nombre, empresa, telefono, email')
          ?.or(`nombre.ilike.%${trimmed}%,empresa.ilike.%${trimmed}%`)
          ?.limit(8);

        const exactClientIds = new Set((exactClients || []).map(c => c?.id));

        // ── 2) Fetch vehicles for exact matches ────────────────────────────────
        let vehicleCountMap = {};
        let vehicleMap = {};

        if (exactClients?.length > 0) {
          const ids = exactClients?.map(c => c?.id);
          const { data: exactVehicles } = await supabase
            ?.from('vehicles')
            ?.select('id, vin, placas, marca, modelo, year, client_id')
            ?.in('client_id', ids)
            ?.limit(50);

          (exactVehicles || [])?.forEach(v => {
            if (!vehicleMap?.[v?.client_id]) vehicleMap[v?.client_id] = [];
            vehicleMap?.[v?.client_id]?.push(v);
            vehicleCountMap[v?.client_id] = (vehicleCountMap?.[v?.client_id] || 0) + 1;
          });

          (exactClients || [])?.forEach(c => {
            const cvs = vehicleMap?.[c?.id] || [];
            if (cvs?.length === 0) {
              found?.push({
                type: 'client',
                clientId: c?.id,
                vehicleId: null,
                vin: '—', placas: '—', marca: '—', modelo: '—', year: '—',
                clienteNombre: c?.nombre || c?.empresa || '',
                clienteTelefono: c?.telefono || '',
                vehicleCount: 0,
                isFuzzy: false,
              });
            } else {
              cvs?.forEach(v => {
                found?.push({
                  type: 'client_vehicle',
                  clientId: c?.id,
                  vehicleId: v?.id,
                  vin: v?.vin || '—',
                  placas: v?.placas || '—',
                  marca: v?.marca || '—',
                  modelo: v?.modelo || '—',
                  year: v?.year || '—',
                  clienteNombre: c?.nombre || c?.empresa || '',
                  clienteTelefono: c?.telefono || '',
                  vehicleCount: cvs?.length,
                  isFuzzy: false,
                });
              });
            }
          });
        }

        // ── 3) Fuzzy fallback if exact results < 3 ────────────────────────────
        let fuzzyFound = [];
        if (exactClients?.length < 3) {
          const { data: broadClients } = await supabase
            ?.from('clients')
            ?.select('id, nombre, empresa, telefono, email')
            ?.order('created_at', { ascending: false })
            ?.limit(200);

          // Score each client not already in exact results
          const scored = (broadClients || [])?.filter(c => !exactClientIds?.has(c?.id))?.map(c => {
              const nameStr = [c?.nombre, c?.empresa]?.filter(Boolean)?.join(' ');
              const score = fuzzyScore(trimmed, nameStr);
              return { client: c, score };
            })?.filter(({ score }) => score > 0.35)?.sort((a, b) => b?.score - a?.score)?.slice(0, 8 - found?.length);

          if (scored?.length > 0) {
            const fuzzyIds = scored?.map(({ client }) => client?.id);
            const { data: fuzzyVehicles } = await supabase
              ?.from('vehicles')
              ?.select('id, vin, placas, marca, modelo, year, client_id')
              ?.in('client_id', fuzzyIds)
              ?.limit(100);

            const fuzzyVehicleMap = {};
            (fuzzyVehicles || [])?.forEach(v => {
              if (!fuzzyVehicleMap?.[v?.client_id]) fuzzyVehicleMap[v?.client_id] = [];
              fuzzyVehicleMap?.[v?.client_id]?.push(v);
            });

            // Build one suggestion per client (not per vehicle) for fuzzy section
            scored?.forEach(({ client: c, score }) => {
              const cvs = fuzzyVehicleMap?.[c?.id] || [];
              fuzzyFound?.push({
                type: 'fuzzy_client',
                clientId: c?.id,
                vehicleId: null,
                vin: '—', placas: '—', marca: '—', modelo: '—', year: '—',
                clienteNombre: c?.nombre || c?.empresa || '',
                clienteTelefono: c?.telefono || '',
                vehicleCount: cvs?.length,
                vehicles: cvs,
                isFuzzy: true,
                score,
              });
            });
          }
        }

        setFuzzyResults(fuzzyFound);
      }

      setResults(found);
      setShowDropdown(true);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInput = (e) => {
    const val = e?.target?.value;
    setQuery(val);
    clearTimeout(debounceRef?.current);
    if (val?.trim()?.length < 2) {
      setResults([]);
      setFuzzyResults([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const handleSelect = (item) => {
    setShowDropdown(false);
    setQuery('');
    if (item?.vehicleId) {
      onSelectVehicle({ vehicleId: item?.vehicleId, clientId: item?.clientId });
    } else if (item?.clientId) {
      onSelectVehicle({ vehicleId: null, clientId: item?.clientId });
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef?.current?.contains(e?.target) && !inputRef?.current?.contains(e?.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const modeLabel = {
    vin: 'VIN',
    placas: 'Placas',
    cliente: 'Cliente',
  };

  const totalCount = results?.length + fuzzyResults?.length;

  return (
    <div className="relative w-full max-w-2xl">
      <div className="relative flex items-center">
        <div className="absolute left-3 text-muted-foreground pointer-events-none">
          {searching ? (
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <Icon name="Search" size={18} />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => (results?.length > 0 || fuzzyResults?.length > 0) && setShowDropdown(true)}
          placeholder="Buscar por VIN, placas o nombre de cliente…"
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
        />
        {query && searchMode && (
          <span className="absolute right-3 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
            {modeLabel?.[searchMode]}
          </span>
        )}
      </div>
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {totalCount === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              Sin resultados para "{query}"
            </div>
          ) : (
            <>
              {/* ── Exact results header ── */}
              {results?.length > 0 && (
                <div className="px-3 py-2 border-b border-border bg-muted/30">
                  <p className="text-xs text-muted-foreground font-medium">
                    {results?.length} resultado{results?.length !== 1 ? 's' : ''} encontrado{results?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              )}

              {/* ── Exact result rows ── */}
              {results?.map((item, idx) => (
                <button
                  key={`exact-${idx}`}
                  onClick={() => handleSelect(item)}
                  className="w-full text-left flex items-center justify-between p-4 hover:bg-muted/50 hover:border-primary/30 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <Icon name={item?.type === 'client' ? 'User' : 'Car'} size={18} className={item?.type === 'client' ? 'text-accent' : 'text-primary'} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{item?.clienteNombre}</p>
                      <p className="text-xs text-muted-foreground">{item?.marca} {item?.modelo} {item?.year}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-primary flex items-center gap-1">
                      <Icon name="Eye" size={12} />
                      Ver detalle
                    </span>
                  </div>
                </button>
              ))}

              {/* ── Fuzzy suggestions section ── */}
              {fuzzyResults?.length > 0 && (
                <>
                  <div className="px-3 py-2 border-b border-border bg-amber-50/60 dark:bg-amber-900/10 flex items-center gap-2">
                    <Icon name="Sparkles" size={13} className="text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-400 font-semibold">
                      Sugerencias aproximadas
                    </p>
                  </div>
                  {fuzzyResults?.map((item, idx) => (
                    <button
                      key={`fuzzy-${idx}`}
                      onClick={() => handleSelect(item)}
                      className="w-full text-left flex items-center justify-between p-4 hover:bg-amber-50/60 dark:hover:bg-amber-900/10 hover:border-primary/30 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-50 rounded-lg group-hover:bg-purple-100 transition-colors">
                          <Icon name="UserSearch" size={16} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{item?.clienteNombre}</p>
                          <p className="text-xs text-muted-foreground">{item?.vehicleCount} unidad{item?.vehicleCount !== 1 ? 's' : ''} registrada{item?.vehicleCount !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-primary flex items-center gap-1">
                          <Icon name="Eye" size={12} />
                          Ver detalle
                        </span>
                      </div>
                    </button>
                  ))}
                </>
              )}

              {/* ── No exact results but fuzzy found ── */}
              {results?.length === 0 && fuzzyResults?.length === 0 && (
                <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                  Sin resultados para "{query}"
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const VehicleHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vehicle_id: paramVehicleId } = useParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [client, setClient] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [completedServices, setCompletedServices] = useState([]);
  const [activeTab, setActiveTab] = useState('servicios');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Detail modals
  const [selectedWO, setSelectedWO] = useState(null);
  const [selectedInsp, setSelectedInsp] = useState(null);

  const vehicleId = paramVehicleId || location?.state?.vehicleId || new URLSearchParams(location?.search)?.get('vehicleId');
  const clientId = location?.state?.clientId || new URLSearchParams(location?.search)?.get('clientId');

  useEffect(() => {
    if (vehicleId) {
      loadVehicleHistory(vehicleId);
    } else if (clientId) {
      loadClientHistory(clientId);
    }
  }, [vehicleId, clientId]);

  const loadVehicleHistory = async (vId) => {
    // Guard: reject undefined, null, or non-UUID strings before querying
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!vId || !UUID_REGEX?.test(vId)) {
      setError('ID de vehículo inválido');
      setIsLoading(false);
      setHasLoaded(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    setHasLoaded(false);
    try {
      const { data: vehicleData, error: vErr } = await supabase
        ?.from('vehicles')
        ?.select('*, client:clients(id, nombre, empresa, telefono, email, rfc)')
        ?.eq('id', vId)
        ?.maybeSingle();

      if (vErr) throw vErr;
      setVehicle(vehicleData);
      if (vehicleData?.client) setClient(vehicleData?.client);

      const { data: historyRecords, error: histErr } = await supabase
        ?.from('vehicle_history')
        ?.select('*')
        ?.eq('vehicle_id', vId)
        ?.eq('type', 'DONE')
        ?.order('service_date', { ascending: false });

      if (!histErr) setCompletedServices(historyRecords || []);

      const { data: wos } = await supabase
        ?.from('work_orders')
        ?.select('id, folio_ot, estado_ot, fecha_ingreso, fecha_entrega, total, notas, motivo_ingreso, kilometraje, created_at, updated_at')
        ?.eq('vehicle_id', vId)
        ?.order('created_at', { ascending: false });
      setWorkOrders(wos || []);

      if (wos?.length > 0) {
        const woIds = wos?.map(w => w?.id);
        const { data: insp } = await supabase
          ?.from('technical_inspections')
          ?.select('id, work_order_id, category, status, findings, recommendations, inspected_at, created_at, updated_at')
          ?.in('work_order_id', woIds)
          ?.order('inspected_at', { ascending: false });
        setInspections(insp || []);

        const { data: quot } = await supabase
          ?.from('quotation_items')
          ?.select('id, work_order_id, description, quantity, unit_price, total_price, is_approved, category, created_at')
          ?.in('work_order_id', woIds)
          ?.order('created_at', { ascending: false });
        setQuotations(quot || []);
      } else {
        setInspections([]);
        setQuotations([]);
      }
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading vehicle history:', err);
      setError('Error al cargar el historial de vehículo');
    } finally {
      setIsLoading(false);
    }
  };

  const loadClientHistory = async (cId) => {
    setIsLoading(true);
    setError(null);
    setHasLoaded(false);
    try {
      const clientData = await clientService?.getById(cId);
      setClient(clientData);
      setVehicle(null);

      const wos = await clientService?.getHistory(cId);
      setWorkOrders(wos || []);
      setCompletedServices([]);
      setInspections([]);
      setQuotations([]);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading client history:', err);
      setError('Error al cargar el historial del cliente');
    } finally {
      setIsLoading(false);
    }
  };

  // Called when user selects a result from the smart search
  const handleSearchSelect = ({ vehicleId: vId, clientId: cId }) => {
    // Reset state
    setVehicle(null);
    setClient(null);
    setWorkOrders([]);
    setInspections([]);
    setQuotations([]);
    setCompletedServices([]);
    setActiveTab('servicios');
    setError(null);

    if (vId) {
      loadVehicleHistory(vId);
    } else if (cId) {
      loadClientHistory(cId);
    }
  };

  const totalFacturado = workOrders?.reduce((sum, wo) => sum + (parseFloat(wo?.total) || 0), 0);
  const totalOTs = workOrders?.length;

  // Recomendación de próximo servicio para el historial general de la unidad
  const proximoServicioGeneral = deriveProximoServicio(workOrders, inspections, vehicle);

  const buildTimeline = () => {
    const events = [];
    workOrders?.forEach(wo => {
      events?.push({
        id: `wo-${wo?.id}`,
        type: 'work_order',
        date: wo?.fecha_ingreso || wo?.created_at,
        title: `OT: ${wo?.folio_ot}`,
        subtitle: wo?.motivo_ingreso || 'Orden de trabajo',
        status: wo?.estado_ot,
        total: wo?.total,
        icon: 'ClipboardList',
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        data: wo,
      });
      if (wo?.fecha_entrega) {
        events?.push({
          id: `del-${wo?.id}`,
          type: 'delivery',
          date: wo?.fecha_entrega,
          title: `Entrega: ${wo?.folio_ot}`,
          subtitle: 'Vehículo entregado al cliente',
          status: 'entregado',
          icon: 'Truck',
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          data: wo,
        });
      }
    });
    inspections?.forEach(insp => {
      events?.push({
        id: `insp-${insp?.id}`,
        type: 'inspection',
        date: insp?.inspected_at,
        title: `Inspección: ${insp?.category}`,
        subtitle: insp?.findings || 'Sin hallazgos registrados',
        status: insp?.status,
        icon: 'Search',
        color: 'text-purple-500',
        bgColor: 'bg-purple-50',
        data: insp,
      });
    });
    return events?.sort((a, b) => new Date(b?.date) - new Date(a?.date));
  };

  const timeline = buildTimeline();

  const headerVehicle = completedServices?.[0]?.vehicle_snapshot || null;
  const headerClient = completedServices?.[0]?.client_snapshot || null;

  const displayVehicle = vehicle || (headerVehicle ? {
    marca: headerVehicle?.marca,
    modelo: headerVehicle?.modelo,
    year: headerVehicle?.year,
    vin: headerVehicle?.vin,
    placas: headerVehicle?.placas,
    color: headerVehicle?.color,
    motor: headerVehicle?.motor,
  } : null);

  const displayClient = client || (headerClient ? {
    nombre: headerClient?.nombre,
    telefono: headerClient?.telefono,
    email: headerClient?.email,
  } : null);

  const tabs = [
    { id: 'servicios', label: 'Servicios Completos', icon: 'CheckCircle', count: completedServices?.length },
    { id: 'timeline', label: 'Timeline', icon: 'Clock', count: timeline?.length },
    { id: 'work_orders', label: 'Órdenes de Trabajo', icon: 'ClipboardList', count: totalOTs },
    { id: 'inspections', label: 'Inspecciones', icon: 'Search', count: inspections?.length },
    { id: 'quotations', label: 'Cotizaciones', icon: 'FileText', count: quotations?.length },
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0">
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="text-xl font-heading font-semibold text-foreground leading-tight">Historial de Vehículo</h1>
                <p className="text-sm text-muted-foreground mt-1">Expediente permanente de servicios realizados</p>
              </div>
            </div>
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate(-1)} className="flex-shrink-0">
              <span className="hidden sm:inline">Volver</span>
              <span className="sm:hidden sr-only">Volver</span>
            </Button>
          </div>

          {/* Smart Search Bar */}
          <div className="px-4 md:px-6 lg:px-8 pb-4">
            <SmartSearch onSelectVehicle={handleSearchSelect} />
            <p className="text-xs text-muted-foreground mt-1 pl-1">
              Busca por <span className="font-medium text-foreground">VIN</span>, <span className="font-medium text-foreground">placas</span> o <span className="font-medium text-foreground">nombre / empresa del cliente</span>
            </p>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          {/* Empty state — no search performed yet */}
          {!isLoading && !hasLoaded && !error && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="p-4 bg-primary/10 rounded-full mb-4">
                <Icon name="Search" size={36} className="text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-2">Busca un vehículo o cliente</h2>
              <p className="text-sm text-muted-foreground max-w-sm">
                Usa el buscador de arriba para encontrar el historial completo por VIN, placas o nombre del cliente.
              </p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground">Cargando historial...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Volver</Button>
            </div>
          ) : hasLoaded ? (
            <div className="max-w-5xl mx-auto space-y-6">
              {/* Vehicle + Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {displayVehicle && (
                  <div className="bg-card rounded-lg border border-border p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-primary/10 rounded-lg"><Icon name="Car" size={22} className="text-primary" /></div>
                      <h3 className="font-semibold text-foreground">Vehículo</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="text-foreground font-medium text-lg">{displayVehicle?.marca} {displayVehicle?.modelo} {displayVehicle?.year}</p>
                      <p className="text-muted-foreground">VIN: <span className="font-mono text-foreground">{displayVehicle?.vin || '—'}</span></p>
                      <p className="text-muted-foreground">Placas: <span className="text-foreground">{displayVehicle?.placas || '—'}</span></p>
                      {displayVehicle?.color && <p className="text-muted-foreground">Color: <span className="text-foreground">{displayVehicle?.color}</span></p>}
                      {displayVehicle?.motor && <p className="text-muted-foreground">Motor: <span className="text-foreground">{displayVehicle?.motor}</span></p>}
                    </div>
                  </div>
                )}
                {displayClient && (
                  <div className="bg-card rounded-lg border border-border p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-accent/10 rounded-lg"><Icon name="User" size={22} className="text-accent" /></div>
                      <h3 className="font-semibold text-foreground">Cliente</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="text-foreground font-medium text-lg">{displayClient?.nombre}</p>
                      {displayClient?.empresa && <p className="text-muted-foreground">{displayClient?.empresa}</p>}
                      <p className="text-muted-foreground">Tel: <span className="text-foreground">{displayClient?.telefono || '—'}</span></p>
                      {displayClient?.email && <p className="text-muted-foreground">Email: <span className="text-foreground">{displayClient?.email}</span></p>}
                    </div>
                  </div>
                )}
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Servicios Completos', value: completedServices?.length, icon: 'CheckCircle', color: 'text-teal-500', bg: 'bg-teal-50' },
                  { label: 'Total OTs', value: totalOTs, icon: 'ClipboardList', color: 'text-blue-500', bg: 'bg-blue-50' },
                  { label: 'Inspecciones', value: inspections?.length, icon: 'Search', color: 'text-purple-500', bg: 'bg-purple-50' },
                  { label: 'Total Facturado', value: `$${totalFacturado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, icon: 'DollarSign', color: 'text-emerald-500', bg: 'bg-emerald-50' },
                ]?.map((stat) => (
                  <div key={stat?.label} className="bg-card rounded-lg border border-border p-4">
                    <div className={`inline-flex p-2 rounded-lg ${stat?.bg} mb-2`}>
                      <Icon name={stat?.icon} size={18} className={stat?.color} />
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                    <p className="text-xs text-muted-foreground">{stat?.label}</p>
                  </div>
                ))}
              </div>

              {/* Próximo Servicio Recomendado — Historial General */}
              {proximoServicioGeneral && (
                <ProximoServicioBlock recomendacion={proximoServicioGeneral} />
              )}

              {/* Tabs */}
              <div className="bg-card rounded-lg border border-border">
                <div className="flex border-b border-border overflow-x-auto">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeTab === tab?.id ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      {tab?.label}
                      <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">{tab?.count}</span>
                    </button>
                  ))}
                </div>

                <div className="p-5">

                  {/* Servicios Completos Tab */}
                  {activeTab === 'servicios' && (
                    <div className="space-y-6">
                      {completedServices?.length === 0 ? (
                        <div className="text-center py-12">
                          <Icon name="ClipboardCheck" size={40} className="text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground font-medium">No hay servicios completados registrados</p>
                          <p className="text-sm text-muted-foreground mt-1">Los servicios aparecerán aquí cuando se complete una Orden de Trabajo</p>
                        </div>
                      ) : (
                        completedServices?.map((svc) => {
                          const parts = Array.isArray(svc?.parts_snapshot) ? svc?.parts_snapshot : [];
                          const labor = Array.isArray(svc?.labor_snapshot) ? svc?.labor_snapshot : [];
                          const svcVehicle = svc?.vehicle_snapshot || {};
                          const svcClient = svc?.client_snapshot || {};
                          const serviceDate = svc?.service_date
                            ? new Date(svc?.service_date)?.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                            : '—';

                          return (
                            <div key={svc?.id} className="border border-border rounded-lg overflow-hidden">
                              <div className="bg-primary/5 border-b border-border px-5 py-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <Icon name="CheckCircle" size={18} className="text-teal-500" />
                                      <span className="font-semibold text-foreground">
                                        {svc?.order_number ? `OT: ${svc?.order_number}` : svc?.title || 'Servicio completado'}
                                      </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      <Icon name="Calendar" size={13} className="inline mr-1" />
                                      {serviceDate}
                                    </p>
                                  </div>
                                  <div className="text-right text-sm">
                                    {svcVehicle?.marca && (
                                      <p className="text-foreground font-medium">
                                        {svcVehicle?.marca} {svcVehicle?.modelo} {svcVehicle?.year}
                                      </p>
                                    )}
                                    {svcVehicle?.vin && (
                                      <p className="text-muted-foreground font-mono text-xs">VIN: {svcVehicle?.vin}</p>
                                    )}
                                    {svcVehicle?.placas && (
                                      <p className="text-muted-foreground text-xs">Placas: {svcVehicle?.placas}</p>
                                    )}
                                    {svcClient?.nombre && (
                                      <p className="text-muted-foreground text-xs mt-1">
                                        <Icon name="User" size={11} className="inline mr-1" />
                                        {svcClient?.nombre}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {svc?.notes && (
                                  <p className="text-sm text-muted-foreground mt-2 italic">Notas: {svc?.notes}</p>
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                                <div className="p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Icon name="Package" size={16} className="text-blue-500" />
                                    <h4 className="font-semibold text-sm text-foreground uppercase tracking-wide">Refacciones</h4>
                                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{parts?.length}</span>
                                  </div>
                                  {parts?.length === 0 ? (
                                    <p className="text-sm text-muted-foreground italic">Sin refacciones registradas</p>
                                  ) : (
                                    <ul className="space-y-2">
                                      {parts?.map((part, idx) => (
                                        <li key={idx} className="text-sm">
                                          <div className="flex items-start gap-2">
                                            <span className="text-muted-foreground font-mono text-xs mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-foreground font-medium leading-snug">{part?.description || '—'}</p>
                                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                                {part?.quantity > 0 && (
                                                  <span className="text-xs text-muted-foreground">Cant: {part?.quantity}</span>
                                                )}
                                                {part?.category && (
                                                  <span className="text-xs text-muted-foreground">Cat: {part?.category}</span>
                                                )}
                                                {part?.supplier && (
                                                  <span className="text-xs text-muted-foreground">Prov: {part?.supplier}</span>
                                                )}
                                                {part?.price > 0 && (
                                                  <span className="text-xs text-emerald-600 font-medium">${parseFloat(part?.price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>

                                <div className="p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Icon name="Wrench" size={16} className="text-orange-500" />
                                    <h4 className="font-semibold text-sm text-foreground uppercase tracking-wide">Mano de Obra</h4>
                                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600">{labor?.length}</span>
                                  </div>
                                  {labor?.length === 0 ? (
                                    <p className="text-sm text-muted-foreground italic">Sin mano de obra registrada</p>
                                  ) : (
                                    <ul className="space-y-2">
                                      {labor?.map((item, idx) => (
                                        <li key={idx} className="text-sm">
                                          <div className="flex items-start gap-2">
                                            <span className="text-muted-foreground font-mono text-xs mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-foreground font-medium leading-snug">{item?.description || '—'}</p>
                                              {item?.hours > 0 && (
                                                <span className="text-xs text-muted-foreground">{item?.hours} {item?.hours === 1 ? 'hora' : 'horas'}</span>
                                              )}
                                            </div>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {/* Timeline Tab */}
                  {activeTab === 'timeline' && (
                    <div className="space-y-0">
                      {timeline?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No hay eventos registrados</p>
                      ) : (
                        <div className="relative">
                          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
                          {timeline?.map((event) => (
                            <div
                              key={event?.id}
                              className={`relative flex gap-4 pb-6 ${(event?.type === 'work_order' || event?.type === 'delivery' || event?.type === 'inspection') ? 'cursor-pointer group' : ''}`}
                              onClick={() => {
                                if (event?.type === 'work_order' || event?.type === 'delivery') setSelectedWO(event?.data);
                                else if (event?.type === 'inspection') setSelectedInsp(event?.data);
                              }}
                            >
                              <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full ${event?.bgColor} flex items-center justify-center border-2 border-background transition-transform ${(event?.type === 'work_order' || event?.type === 'delivery' || event?.type === 'inspection') ? 'group-hover:scale-110' : ''}`}>
                                <Icon name={event?.icon} size={18} className={event?.color} />
                              </div>
                              <div className={`flex-1 bg-muted/30 rounded-lg p-4 min-w-0 transition-colors ${(event?.type === 'work_order' || event?.type === 'delivery' || event?.type === 'inspection') ? 'group-hover:bg-muted/60' : ''}`}>
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <p className="font-medium text-foreground text-sm">{event?.title}</p>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {event?.status && (
                                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS?.[event?.status] || INSP_STATUS_COLORS?.[event?.status] || 'bg-gray-100 text-gray-700'}`}>
                                        {STATUS_LABELS?.[event?.status] || INSP_STATUS_LABELS?.[event?.status] || event?.status}
                                      </span>
                                    )}
                                    {(event?.type === 'work_order' || event?.type === 'delivery' || event?.type === 'inspection') && (
                                      <span className="text-xs text-primary flex items-center gap-1">
                                        <Icon name="Eye" size={12} />
                                        Ver detalle
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="text-xs text-muted-foreground mb-1">{event?.subtitle}</p>
                                <p className="text-xs text-muted-foreground">
                                  <Icon name="Calendar" size={10} className="inline mr-1" />
                                  {event?.date ? fmtDate(event?.date) : ''}
                                  {event?.date && (
                                    <span className="ml-2">
                                      <Icon name="Clock" size={10} className="inline mr-1" />
                                      {fmtTime(event?.date)}
                                    </span>
                                  )}
                                </p>
                                {event?.total > 0 && (
                                  <p className="text-xs font-medium text-emerald-600 mt-1">Total: ${parseFloat(event?.total)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Work Orders Tab */}
                  {activeTab === 'work_orders' && (
                    <div className="space-y-3">
                      {workOrders?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No hay órdenes de trabajo</p>
                      ) : workOrders?.map(wo => (
                        <button
                          key={wo?.id}
                          onClick={() => setSelectedWO(wo)}
                          className="w-full text-left flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/60 hover:border-primary/30 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                              <Icon name="ClipboardList" size={18} className="text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{wo?.folio_ot}</p>
                              <p className="text-xs text-muted-foreground">{wo?.motivo_ingreso || 'Sin motivo'}</p>
                              <p className="text-xs text-muted-foreground">
                                <Icon name="Calendar" size={10} className="inline mr-1" />
                                {wo?.fecha_ingreso ? fmtDate(wo?.fecha_ingreso) : ''}
                                {wo?.fecha_ingreso && (
                                  <span className="ml-2">
                                    <Icon name="Clock" size={10} className="inline mr-1" />
                                    {fmtTime(wo?.fecha_ingreso)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS?.[wo?.estado_ot] || 'bg-gray-100 text-gray-700'}`}>
                                {STATUS_LABELS?.[wo?.estado_ot] || wo?.estado_ot}
                              </span>
                              {wo?.total > 0 && <p className="text-sm font-medium text-emerald-600 mt-1">${parseFloat(wo?.total)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>}
                            </div>
                            <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Inspections Tab */}
                  {activeTab === 'inspections' && (
                    <div className="space-y-3">
                      {inspections?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No hay inspecciones registradas</p>
                      ) : inspections?.map(insp => (
                        <button
                          key={insp?.id}
                          onClick={() => setSelectedInsp(insp)}
                          className="w-full text-left p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/60 hover:border-primary/30 transition-colors group"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-50 rounded-lg group-hover:bg-purple-100 transition-colors">
                                <Icon name="Search" size={16} className="text-purple-600" />
                              </div>
                              <p className="font-medium text-foreground">{insp?.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                insp?.status === 'bueno' ? 'bg-green-100 text-green-700' :
                                insp?.status === 'requiere_atencion' ? 'bg-yellow-100 text-yellow-700' :
                                insp?.status === 'critico' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                              }`}>{INSP_STATUS_LABELS?.[insp?.status] || insp?.status?.replace('_', ' ')}</span>
                              <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                          </div>
                          {insp?.findings && <p className="text-sm text-muted-foreground ml-11 line-clamp-1">Hallazgos: {insp?.findings}</p>}
                          {insp?.inspected_at && (
                            <p className="text-xs text-muted-foreground ml-11 mt-1">
                              <Icon name="Calendar" size={10} className="inline mr-1" />
                              {fmtDate(insp?.inspected_at)}
                              <span className="ml-2">
                                <Icon name="Clock" size={10} className="inline mr-1" />
                                {fmtTime(insp?.inspected_at)}
                              </span>
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Quotations Tab */}
                  {activeTab === 'quotations' && (
                    <div className="space-y-3">
                      {quotations?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No hay cotizaciones registradas</p>
                      ) : quotations?.map(q => (
                        <div key={q?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
                          <div>
                            <p className="font-medium text-foreground">{q?.description}</p>
                            <p className="text-xs text-muted-foreground">Cantidad: {q?.quantity} · Precio unitario: ${parseFloat(q?.unit_price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-foreground">${parseFloat(q?.total_price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <span className={`text-xs ${q?.is_approved ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {q?.is_approved ? 'Aprobado' : 'Pendiente'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                </div>
              </div>
            </div>
          ) : null}
        </main>
      </div>

      {/* OT Detail Modal */}
      {selectedWO && (
        <OTDetailModal wo={selectedWO} onClose={() => setSelectedWO(null)} inspections={inspections} vehicle={vehicle} client={client} navigate={navigate} />
      )}
    </div>
  );
};

export default VehicleHistory;
